import { useMemo, memo } from 'react';
import ReactDOM from 'react-dom';
import type { RefinementListProps } from 'react-instantsearch';

import FilterAccordion from '@klv/atoms/filter-accordion';
import { generateClassName, windowIsDefined } from '@klv/shared/utils/functions';

import UnifiedSearchRefinementList from './unified-search-refinement-list';
import type { IRefinementsPortalProps, IRefinement  } from './types';
import useUnifiedSearchIndexes from './hooks/use-unified-search-indexes';

import './styles/unified-search-refinement.css';
import * as s from './styles/unified-search.module.css';

/* We have to memoize the transformItems function or we will get infinte loops */
const MemoizedRefinementList = ({ backgroundColor = 'light', refinement }: { backgroundColor: IRefinementsPortalProps['backgroundColor']; refinement: IRefinement; }) => {
  const { transformItems, ...rest } = refinement;
  const memoizeTransform = useMemo(() => transformItems, [transformItems]);
  
  const createSortBy = (order: string[]): RefinementListProps['sortBy'] => {
    return (a, b) => {
      const orderA = order.indexOf(a.escapedValue);
      const orderB = order.indexOf(b.escapedValue);
  
      // Prioritize custom order
      if (orderA !== -1 && orderB !== -1) return orderA - orderB;
      if (orderA !== -1) return -1;
      if (orderB !== -1) return 1;
  
      return a.escapedValue.localeCompare(b.escapedValue);
    };
  };

  const customOrders: { [key: string]: string[]; } = {
    'level' :['Beginner', 'Intermediate', 'Advanced'],
    'duration': ['Under 10 minutes', '10 - 30 minutes', '30 minutes - 1 hour', '1+ hours'],
  };

  return (
    <FilterAccordion
      className={generateClassName([
        s[backgroundColor],
        s.filterAccordion,
        'filterAccordion',
        'includeDesktop',
      ])}
      key={JSON.stringify(refinement)}
      label={refinement.label}
    >
      <UnifiedSearchRefinementList 
        {...rest} 
        backgroundColor={backgroundColor}
        limit={100} 
        transformItems={memoizeTransform} 
        sortBy={(refinement.label === 'Duration' || refinement.label === 'Level') ? createSortBy(customOrders[(refinement.label.toLowerCase())]) : ['name']}
      />
    </FilterAccordion>
  );
};

// mount our refinmentsList in a portal
const RefinementsPortal = ({ backgroundColor = 'light', resource }: IRefinementsPortalProps) => {
  
  const containerRoot = windowIsDefined() ? document.getElementById('search-filters-portal') as HTMLElement : null;

  const indexes = useUnifiedSearchIndexes();
  const selectedIndex = indexes.find((index) => index.value === resource);

  if (!containerRoot) return null;
  return ReactDOM.createPortal(
    (<>
      {selectedIndex?.refinements.map((refinement) => (
        <MemoizedRefinementList backgroundColor={backgroundColor} key={refinement.label} refinement={refinement} />
      ))}
    </>),
    containerRoot,
  );
};

export default memo(RefinementsPortal);
